import './UserSurveyAnswersModal.scss';
import { Modal } from "../../Smart/Modal/Modal";
import React, { useContext } from "react";
import { getUserQuestionnaireAnswersAPI } from "../../../Services/api";
import { I18nReactContext } from "../../../contexts/I18nContext";
import { GridReactContextProvider } from "../../../Utils/DataGridWrapper/GridContext";
import { DataGridWrapper } from "../../../Utils/DataGridWrapper/DataGridWrapper";
import { get, map, join } from "lodash";
import {questionnaireQuestionTypes} from '../../Questionnaires/QuestionnaireConstants';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';

/**
 * Display formatted answer value based on question type and data structure from sanitizeQuestionsForClientSession
 * @param {object} params - Parameters object
 * @param {object} params.question - Question object with embedded answer data
 * @param {object} params.i18n - Internationalization object
 * @returns {string} Formatted answer display value
 */
const getAnswerDisplayValue = ({ question, i18n }) => {
    const questionType = get(question, 'type');

    if (questionType === questionnaireQuestionTypes.FREE_TEXT) {
        const userAnswer = get(question, 'userAnswer', get(question, 'textResponse', ''));
        if (userAnswer === null || userAnswer === undefined) {
            return i18n.translate('userSurveyAnswers.noAnswer');
        }
        
        const cleanText = String(userAnswer).trim();
        if (cleanText.length === 0) {
            return i18n.translate('userSurveyAnswers.noAnswer');
        }
        return cleanText;
    }
    
    if (questionType === questionnaireQuestionTypes.RATING_SCALE) {
        const userAnswer = get(question, 'userAnswer', get(question, 'ratingValue', null));
        
        if (userAnswer === null || userAnswer === undefined) {
            return i18n.translate('userSurveyAnswers.noAnswer');
        }
        
        const rating = Number(userAnswer);
        if (isNaN(rating)) {
            return i18n.translate('userSurveyAnswers.invalidRating');
        }
        
        const ratingConfig = get(question, 'ratingScaleConfig', {});
        const minValue = get(ratingConfig, 'minValue', 1);
        const maxValue = get(ratingConfig, 'maxValue', 5);
        
        let ratingDisplay = `${rating}`;
        if (minValue && maxValue) {
            ratingDisplay += ` (${minValue}-${maxValue})`;
        }
        return ratingDisplay;
    }
    
    if (questionType === questionnaireQuestionTypes.MULTIPLE_CHOICE || questionType === questionnaireQuestionTypes.CHECKBOX) {
        // For MULTIPLE_CHOICE and CHECKBOX, answers are in userAnswers field (plural)
        const userAnswers = get(question, 'userAnswers', []);
        
        if (!Array.isArray(userAnswers) || userAnswers.length === 0) {
            return i18n.translate('userSurveyAnswers.noAnswer');
        }
        
        const questionOptions = get(question, 'options', []);
        const selectedOptionTexts = map(userAnswers, (optionId) => {
            const option = questionOptions.find(opt => opt.id === optionId);
            return option ? option.text : `${i18n.translate('userSurveyAnswers.unknownOption')} (${optionId})`;
        });
        
        if (selectedOptionTexts.length === 0) {
            return i18n.translate('userSurveyAnswers.noAnswer');
        }
        
        return join(selectedOptionTexts, ', ');
    }
    
    return i18n.translate('userSurveyAnswers.noAnswer');
};

/**
 * Gets the display text for a question type
 * @param {string} questionType - The question type constant
 * @param {object} i18n - Internationalization object
 * @returns {string} Translated question type text
 */
const getQuestionTypeDisplay = (questionType, i18n) => {
    switch (questionType) {
        case questionnaireQuestionTypes.FREE_TEXT:
            return i18n.translate("questionnaireQuestions.questionOptions.FREE_TEXT");
        case questionnaireQuestionTypes.MULTIPLE_CHOICE:
            return i18n.translate("questionnaireQuestions.questionOptions.MULTIPLE_CHOICE");
        case questionnaireQuestionTypes.CHECKBOX:
            return i18n.translate("questionnaireQuestions.questionOptions.CHECKBOX");
        case questionnaireQuestionTypes.RATING_SCALE:
            return i18n.translate("questionnaireQuestions.questionOptions.RATING_SCALE");
        default:
            return i18n.translate("userSurveyAnswers.unknownQuestionType");
    }
};

/**
 * Gets the columns configuration for the user survey answers table
 * @param {object} params - Function parameters
 * @param {object} params.i18n - Internationalization object
 * @returns {Array} Array of column configurations
 */
const getUserSurveyAnswersColumns = ({ i18n }) => {
    return [
        {
            field: 'questionText',
            headerName: i18n.translate("userSurveyAnswers.columns.question"),
            minWidth: 300,
            flex: 2,
            align: 'right',
            sortable: false,
            renderCell: (params) => (
                <div style={{ width: '100%', padding: '8px 0', lineHeight: '1.4' }} title={params.value}>
                    {params.value}
                </div>
            ),
        },
        {
            field: 'questionType',
            headerName: i18n.translate("userSurveyAnswers.columns.questionType"),
            minWidth: 150,
            flex: 1,
            align: 'right',
            sortable: false,
            renderCell: (params) => (
                <div style={{ width: '100%', padding: '8px 0', lineHeight: '1.4' }} title={params.value}>
                    {params.value}
                </div>
            ),
        },
        {
            field: 'userAnswer',
            headerName: i18n.translate("userSurveyAnswers.columns.userAnswer"),
            minWidth: 250,
            flex: 2,
            align: 'right',
            sortable: false,
            renderCell: (params) => (
                <div style={{ width: '100%', padding: '8px 0', lineHeight: '1.4' }} title={params.value}>
                    {params.value}
                </div>
            ),
        },
        {
            field: 'isCorrect',
            headerName: i18n.translate("userSurveyAnswers.columns.isCorrect"),
            minWidth: 120,
            flex: 0.7,
            align: 'center',
            headerAlign: 'center',
            sortable: false,
            renderCell: (params) => {
                const value = params.value;
                if (value === null || value === undefined) {
                    return (
                        <div className="UserSurveyAnswersModal__not-applicable">
                            <RemoveCircleOutlineIcon className="UserSurveyAnswersModal__not-applicable-icon" />
                        </div>
                    );
                }
                return (
                    <div style={{ padding: '8px 0', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        {value ? (
                            <CheckCircleOutlineIcon className="UserSurveyAnswersModal__correct-icon" />
                        ) : (
                            <HighlightOffIcon className="UserSurveyAnswersModal__incorrect-icon" />
                        )}
                    </div>
                );
            },
        },
    ];
};

const tableFilters = {
    showFilters: {
        search: false,
        organizations: false
    },
    filterValues: {},
    filterData: {}
};

/**
 * Converts the API response data to the format expected by DataGridWrapper
 * @param {object} apiResponse - The response from getUserSurveyAnswersAPI
 * @param {object} i18n - Internationalization object
 * @returns {Array} Array of formatted data for the grid
 */
const convertData = (apiResponse, i18n) => {
    const questions = get(apiResponse, 'questions', []);

    return map(questions, (question, index) => {
        const answerDisplay = getAnswerDisplayValue({ question, i18n });
        const questionType = get(question, 'type');
        const questionTypeDisplay = getQuestionTypeDisplay(questionType, i18n);

        // answerIsCorrect: 1 = correct, 0 = incorrect, null = not applicable (free text, rating, unanswered)
        const nonScorableTypes = [questionnaireQuestionTypes.FREE_TEXT, questionnaireQuestionTypes.RATING_SCALE];
        const userAnswers = get(question, 'userAnswers', []);
        const hasAnswer = Array.isArray(userAnswers) && userAnswers.length > 0;
        const answerIsCorrect = nonScorableTypes.includes(questionType) || !hasAnswer
            ? null
            : get(question, 'answerIsCorrect', null);
        const isCorrect = answerIsCorrect != null
            ? parseFloat(answerIsCorrect) === 1
            : null;

        return {
            id: question.id || `question-${index}`,
            questionText: question.text || i18n.translate('userSurveyAnswers.unknownQuestion'),
            questionType: questionTypeDisplay,
            userAnswer: answerDisplay,
            isCorrect,
            question: question,
            answer: answerDisplay
        };
    });
};

const paginationModel = {
    page: 0,
    pageSize: 100,
};

/**
 * UserSurveyAnswersModal Component
 * 
 * Displays a modal showing the specific answers a user provided for a survey.
 * Uses DataGridWrapper to show questions and their corresponding user answers.
 * 
 * @param {object} params - Component parameters
 * @param {boolean} params.modalOpen - Whether the modal is open
 * @param {function} params.closeModal - Function to close the modal
 * @param {string} params.userId - The ID of the user whose answers to show
 * @param {string} params.surveyId - The ID of the survey
 * @param {object} params.user - The user object containing firstName and lastName for the modal title
 * @param {boolean} [params.includeCorrectAnswer] - Whether to include correct answer data
 * @returns {JSX.Element} Rendered user survey answers modal component
 */
export const UserSurveyAnswersModal = ({
    modalOpen,
    closeModal,
    userId,
    surveyId,
    user,
    includeCorrectAnswer
}) => {
    const { i18n } = useContext(I18nReactContext);

    /**
     * Gets the columns configuration for the data grid
     * @returns {Promise<Array>} Promise that resolves to array of column configurations
     */
    const getColumns = async () => {
        return getUserSurveyAnswersColumns({ i18n });
    };

    /**
     * API call to get the user's survey answers
     * @returns {Promise<object>} Promise that resolves to the API response with user answers
     */
    const apiToGetData = async () => {
        try {
            const response = await getUserQuestionnaireAnswersAPI({
                targetUserId: userId,
                questionnaireId: surveyId,
                includeCorrectAnswer
            });

            const questions = get(response, 'questions', []);
            return { data: response, count: questions.length };
        } catch (error) {
            console.error('Error fetching user survey answers:', error);
            return { data: { questions: [] }, count: 0 };
        }
    };

    const userName = `${get(user, 'firstName', '')} ${get(user, 'lastName', '')}`.trim();
    const modalTitle = i18n.translate('userSurveyAnswers.modalTitle', { 
        context: { userName } 
    });

    return (
        <Modal open={modalOpen} onClose={closeModal}>
            <div className="UserSurveyAnswersModal modalWrapper">
                <div className="modalTitle">
                    {modalTitle}
                </div>
                <div className="modalContent">
                    <GridReactContextProvider initFilterDisabled={{}}>
                        <DataGridWrapper
                            filterModel={tableFilters}
                            disableColumnFilter
                            paginationModel={paginationModel}
                            sortModel={[]}
                            buttons={null}
                            checkboxSelection={false}
                            onSelectionModelChange={null}
                            selectionModel={[]}
                            isRowSelectable={null}
                            getColumns={getColumns}
                            apiToGetData={apiToGetData}
                            dataConverter={(apiResponse) => convertData(apiResponse, i18n)}
                        />
                    </GridReactContextProvider>
                </div>
            </div>
        </Modal>
    );
}; 